import React from "react";
import { Badge, Box, Flex, Icon, Text, useColorModeValue } from "@chakra-ui/react";

/**
 * Renders either a Badge or a text value for a metric.
 * @param {object} props
 * @param {string|number} props.value - Text value to display
 * @param {string} [props.chipScheme] - Badge color scheme when in chip mode
 * @param {string} [props.chipLabel] - Badge label when in chip mode
 * @returns {React.ReactElement}
 */
function MetricValue({ value, chipScheme, chipLabel }) {
    const valColor = useColorModeValue("secondaryGray.900", "white");
    if (chipScheme) {
        return <Badge colorScheme={chipScheme} borderRadius="6px" px="8px" fontWeight="700">{chipLabel}</Badge>;
    }
    return <Text fontSize="lg" fontWeight="700" color={valColor}>{value}</Text>;
}

/**
 * Renders a single performance metric tile with an icon, label, and value or badge.
 * @param {object} props
 * @param {React.ElementType} props.icon - react-icons component to display.
 * @param {string} props.iconColor - Color token for the icon.
 * @param {string} props.label - Metric label text.
 * @param {string|number} props.value - Metric display value (text mode).
 * @param {string} [props.chipScheme] - If set, renders a Badge instead of text.
 * @param {string} [props.chipLabel] - Badge label when chip mode.
 * @returns {React.ReactElement}
 */
function PerformanceMetricCard({ icon, iconColor, label, value, chipScheme, chipLabel }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box p="14px" bg={bg} borderRadius="12px" h="100%">
            <Flex align="center" gap="8px" mb="8px">
                <Icon as={icon} w="16px" h="16px" color={iconColor} />
                <Text fontSize="xs" fontWeight="600" color={labelColor} textTransform="uppercase">{label}</Text>
            </Flex>
            <MetricValue value={value} chipScheme={chipScheme} chipLabel={chipLabel} />
        </Box>
    );
}

export default PerformanceMetricCard;
